import { useState, useEffect, FormEvent } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Lock, ShieldCheck, ArrowRight, Sparkles, CheckCircle } from "lucide-react";
import { api, ApiError } from "@/services/api";

function ParticleField() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(40)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-white rounded-full"
          initial={{
            x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 1400),
            y: Math.random() * (typeof window !== "undefined" ? window.innerHeight : 900),
            opacity: Math.random() * 0.3 + 0.1,
            scale: Math.random() * 0.5 + 0.5,
          }}
          animate={{
            y: [null, Math.random() * (typeof window !== "undefined" ? window.innerHeight : 900)],
            opacity: [null, Math.random() * 0.3 + 0.1, 0],
          }}
          transition={{
            duration: Math.random() * 15 + 10,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
}

function GradientOrbs() {
  return (
    <>
      <motion.div
        className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-amber-500/20 rounded-full blur-[100px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#124C77]/40 rounded-full blur-[120px]"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.25, 0.4],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect to login after success
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => navigate("/login"), 3000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      await api.resetPassword(token!, password);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Failed to reset password. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#063F6B] via-[#124C77] to-[#042a4d] flex items-center justify-center p-4">
      <ParticleField />
      <GradientOrbs />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/">
            <img
              src="/logo-light.png"
              alt="Feedforward AI"
              className="h-12 w-auto mx-auto mb-6 hover:scale-105 transition-transform"
            />
          </Link>
        </motion.div>

        {/* Card */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-[#124C77]/20 to-[#EAA059]/20 rounded-2xl blur-xl" />

          <div className="relative bg-[#063F6B]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
            {!token ? (
              /* No token in URL */
              <motion.div
                className="text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-8 h-8 text-red-400" />
                </div>
                <h1
                  className="text-3xl text-white mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                >
                  Invalid Link
                </h1>
                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                  This password reset link is invalid. Please request a new one.
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white font-medium transition-all"
                >
                  Request New Link
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ) : success ? (
              /* Success state */
              <motion.div
                className="text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h1
                  className="text-3xl text-white mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                >
                  Password Reset
                </h1>
                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                  Your password has been successfully reset. Redirecting to login...
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white font-medium transition-all"
                >
                  Go to Login
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ) : (
              <>
                {/* Header */}
                <div className="text-center mb-8">
                  <motion.div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-medium text-amber-400">New Password</span>
                  </motion.div>

                  <h1
                    className="text-3xl text-white mb-2"
                    style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                  >
                    Reset Password
                  </h1>
                  <p className="text-slate-400 text-sm">
                    Enter your new password below
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-1">
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-amber-400 transition-colors">
                        <Lock className="w-5 h-5" />
                      </div>
                      <input
                        id="password"
                        type="password"
                        placeholder="New password (min. 8 characters)"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        minLength={8}
                        className={`w-full pl-12 pr-4 py-4 bg-white/5 border ${
                          error && error.toLowerCase().includes("8 characters") ? "border-red-500/50" : "border-white/10"
                        } rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50 focus:bg-white/10 transition-all duration-300 disabled:opacity-50`}
                      />
                    </div>
                    {error && error.toLowerCase().includes("8 characters") && (
                      <p className="text-red-400 text-xs pl-4">{error}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-amber-400 transition-colors">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        minLength={8}
                        className={`w-full pl-12 pr-4 py-4 bg-white/5 border ${
                          error && error.toLowerCase().includes("match") ? "border-red-500/50" : "border-white/10"
                        } rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50 focus:bg-white/10 transition-all duration-300 disabled:opacity-50`}
                      />
                    </div>
                    {error && error.toLowerCase().includes("match") && (
                      <p className="text-red-400 text-xs pl-4">{error}</p>
                    )}
                  </div>

                  {error &&
                   !error.toLowerCase().includes("8 characters") &&
                   !error.toLowerCase().includes("match") && (
                    <motion.div
                      className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {error}
                      {(error.toLowerCase().includes("expired") || error.toLowerCase().includes("invalid") || error.toLowerCase().includes("already been used")) && (
                        <Link
                          to="/forgot-password"
                          className="block mt-2 text-amber-400 hover:text-amber-300 transition-colors"
                        >
                          Request a new reset link
                        </Link>
                      )}
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {isLoading ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Resetting...
                        </>
                      ) : (
                        <>
                          Reset Password
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700"
                      initial={{ x: "100%" }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.button>
                </form>

                {/* Footer */}
                <div className="mt-8 text-center">
                  <p className="text-slate-400 text-sm">
                    Remember your password?{" "}
                    <Link
                      to="/login"
                      className="text-amber-400 hover:text-amber-300 font-medium transition-colors"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Back to home link */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Link
            to="/"
            className="text-slate-500 hover:text-white text-sm transition-colors inline-flex items-center gap-1"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to home
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
